import axios from 'axios';
import { API_ASSET_BASE_URL, API_BASE_URL } from '../config/env';

const PUBLIC_URL = process.env.PUBLIC_URL || '';
const FALLBACK_IMAGE = `${PUBLIC_URL}/arkab/ce1dc7_9a43df1a46cb44379575b67da6f9e208-mv2.jpg`;
const publicApi = axios.create({ baseURL: API_BASE_URL });

export type StoreCategory = {
  _id: string;
  name: string;
  imageUrl?: string;
  showOnHome?: boolean;
  displayOrder?: number;
};

export type StoreProduct = {
  _id: string;
  name: string;
  description?: string;
  price: number;
  discount?: number;
  images?: string[];
  sku?: string;
  subcategory?: string;
  sizes?: string[];
  stock?: number;
  category?: StoreCategory;
  categoryId?: string;
};

type RawProduct = {
  _id: string;
  name: string;
  description?: string;
  price: number;
  discount?: number;
  images?: string[];
  sku?: string;
  subcategory?: string;
  sizes?: string[];
  stock?: number;
  category?: {
    _id: string;
    name: string;
  };
};

type ProductListResponse = {
  products: RawProduct[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
};

export type StoreProductsQuery = {
  page?: number;
  limit?: number;
  category?: string;
  subcategory?: string;
  sizes?: string;
  sortBy?: 'createdAt' | 'price' | 'name' | 'subcategory';
  sortOrder?: 'asc' | 'desc';
  minPrice?: number;
  maxPrice?: number;
};

export type StoreProductsPage = {
  products: StoreProduct[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
};

const normalizeProduct = (product: RawProduct): StoreProduct => {
  return {
    _id: String(product._id),
    name: String(product.name),
    description: String(product.description || ''),
    price: Number(product.price || 0),
    discount: Number(product.discount || 0),
    images: Array.isArray(product.images) ? product.images : [],
    sku: product.sku ? String(product.sku) : undefined,
    subcategory: product.subcategory ? String(product.subcategory) : undefined,
    sizes: Array.isArray(product.sizes) ? product.sizes.map((size) => String(size)) : [],
    stock: Number(product.stock ?? 99),
    category: product.category
      ? {
          _id: String(product.category._id),
          name: String(product.category.name),
        }
      : undefined,
    categoryId: product.category ? String(product.category._id) : undefined,
  };
};

export const fetchStoreProducts = async (limit = 100): Promise<StoreProduct[]> => {
  const response = await publicApi.get<ProductListResponse>('/products', {
    params: {
      page: 1,
      limit,
    },
  });

  const products = Array.isArray(response.data?.products) ? response.data.products : [];
  return products.map(normalizeProduct);
};

export const fetchStoreProductsPage = async (query: StoreProductsQuery): Promise<StoreProductsPage> => {
  const response = await publicApi.get<ProductListResponse>('/products', { params: query });
  const products = Array.isArray(response.data?.products) ? response.data.products : [];

  return {
    products: products.map(normalizeProduct),
    total: Number(response.data?.total ?? 0),
    page: Number(response.data?.page ?? query.page ?? 1),
    limit: Number(response.data?.limit ?? query.limit ?? 12),
    totalPages: Number(response.data?.totalPages ?? 0),
  };
};

export const fetchStoreCategories = async (): Promise<StoreCategory[]> => {
  const response = await publicApi.get('/categories', {
    params: {
      page: 1,
      limit: 100,
      isActive: true,
      sortBy: 'name',
      sortOrder: 'asc',
    },
  });

  const list = Array.isArray(response.data?.categories)
    ? response.data.categories
    : Array.isArray(response.data)
      ? response.data
      : [];

  return list
    .filter((category: any) => category?._id && category?.name)
    .map((category: any) => ({
      _id: String(category._id),
      name: String(category.name),
      imageUrl: category.imageUrl ? String(category.imageUrl) : undefined,
      showOnHome: typeof category.showOnHome === 'boolean' ? category.showOnHome : undefined,
      displayOrder: Number(category.displayOrder ?? 0),
    }));
};

export const fetchHomeCategories = async (): Promise<StoreCategory[]> => {
  const response = await publicApi.get('/categories', {
    params: {
      page: 1,
      limit: 100,
      isActive: true,
      showOnHome: true,
      sortBy: 'displayOrder',
      sortOrder: 'asc',
    },
  });

  const list = Array.isArray(response.data?.categories)
    ? response.data.categories
    : Array.isArray(response.data)
      ? response.data
      : [];

  return list
    .filter((category: any) => category?._id && category?.name)
    .map((category: any) => ({
      _id: String(category._id),
      name: String(category.name),
      imageUrl: category.imageUrl ? String(category.imageUrl) : undefined,
      showOnHome: true,
      displayOrder: Number(category.displayOrder ?? 0),
    }));
};

export const fetchStoreProductById = async (id: string): Promise<StoreProduct> => {
  const response = await publicApi.get<RawProduct>(`/products/${id}`);
  return normalizeProduct(response.data);
};

export const getEffectivePrice = (product: StoreProduct) => {
  const discount = Number(product.discount ?? 0);
  return discount > 0 ? Number(product.price) * (1 - discount / 100) : Number(product.price);
};

export const resolveProductImage = (imagePath?: string) => {
  if (!imagePath) return FALLBACK_IMAGE;
  if (imagePath.startsWith('http')) return imagePath;
  if (imagePath.startsWith('/arkab/')) return `${PUBLIC_URL}${imagePath}`;
  if (imagePath.startsWith('/uploads/')) return `${API_ASSET_BASE_URL}${imagePath}`;
  return `${API_ASSET_BASE_URL}/${imagePath.replace(/^\/+/, '')}`;
};

export const resolveCategoryImage = (imagePath?: string) => {
  if (!imagePath) return FALLBACK_IMAGE;
  if (imagePath.startsWith('http')) return imagePath;
  if (imagePath.startsWith('/arkab/')) return `${PUBLIC_URL}${imagePath}`;
  if (imagePath.startsWith('/uploads/')) return `${API_ASSET_BASE_URL}${imagePath}`;
  return `${API_ASSET_BASE_URL}/${imagePath.replace(/^\/+/, '')}`;
};

export const getFallbackProductImage = () => FALLBACK_IMAGE;
